/*
   Form state for the intake sheet.

   Held as one object rather than a field per `useState`: the sheet spans three
   steps, and a setter per field crowds out the part of the container worth
   reading, which is the three calls behind it and what they mean.
*/

import * as React from "react"

import { type DetectorProfile } from "@/components/domain/detector-profile"
import { type DateStr } from "@/components/ui/date-picker"
import { type DropzoneStatus } from "@/components/ui/file-dropzone"

import { type BatchChoice } from "./batch-choice"

export interface IntakeForm {
  printedFile: File | null
  printedStatus: DropzoneStatus
  /** Product name read off the label — confirmation the right file was picked. */
  identifiedName: string | null
  regNo: string
  printedOn: DateStr | null
  /** The registration a master search has actually run for, not what is typed. */
  searched: string
  /** PPLS masters attached, by URL, in pick order. The first governs. */
  selectedMasterIds: string[]
  /** Uploaded masters, in attach order. A label can draw on more than one. */
  masterFiles: File[]
  masterStatus: DropzoneStatus
  clientId: string | null
  batch: BatchChoice
  /** Set away from Automatic only where the reviewer chose to. Empty is all Automatic. */
  detectorProfile: DetectorProfile
}

const EMPTY: IntakeForm = {
  printedFile: null,
  printedStatus: "idle",
  identifiedName: null,
  regNo: "",
  printedOn: null,
  searched: "",
  selectedMasterIds: [],
  masterFiles: [],
  masterStatus: "idle",
  clientId: null,
  batch: undefined,
  detectorProfile: {},
}

export interface IntakeFormApi {
  form: IntakeForm
  set: (patch: Partial<IntakeForm>) => void
  /**
   * Fill only the fields still untouched. What the label reader returns goes
   * through here: it fills blanks, it does not correct people.
   */
  fillIfBlank: (patch: Partial<IntakeForm>) => void
  reset: () => void
}

export function useIntakeForm(): IntakeFormApi {
  const [form, setForm] = React.useState<IntakeForm>(EMPTY)

  const set = React.useCallback((patch: Partial<IntakeForm>) => {
    setForm((current) => ({ ...current, ...patch }))
  }, [])

  const fillIfBlank = React.useCallback((patch: Partial<IntakeForm>) => {
    setForm((current) => {
      const next = { ...current }
      for (const [key, value] of Object.entries(patch) as [keyof IntakeForm, unknown][]) {
        // Untouched is whatever the field's empty value is — null, an empty
        // string, or undefined for the tri-state. Anything else the reviewer
        // typed or chose, and is not ours to replace.
        const held = current[key]
        const untouched = held === null || held === undefined || held === ""
        if (untouched) Object.assign(next, { [key]: value })
      }
      return next
    })
  }, [])

  const reset = React.useCallback(() => {
    setForm(EMPTY)
  }, [])

  return { form, set, fillIfBlank, reset }
}
